/**
 * Resolver — turns a customer business profile into a flat, tier-tagged,
 * deduped list of panels to render.
 *
 * Pure function. No React. Easy to unit-test.
 *
 * Input:
 *   profile = {
 *     primary_business_model: "manufacturing" | ... | null,
 *     additional_models:      string[] (other business-type ids),
 *     modifier_flags:         { imports: bool, exports: bool, ... },
 *   }
 *
 * Output: Array of resolved panel objects:
 *   { id, label, source, copy, tier, originType?, originFlag? }
 *
 * Tiers (used by the UI to decide prominence / grouping):
 *   "core"      — universal, always rendered first
 *   "primary"   — from the primary business type (and its inherits)
 *   "secondary" — from additional_models[] (collapsed group per type)
 *   "modifier"  — from modifier_flags (e.g. imports → forex)
 *   "strategic" — new-age parameters layer (CCC trend, AI risk, etc.)
 *
 * Dedup rule: first occurrence of a panel `id` wins. Manufacturing → EMS
 * inherits flow keeps the manufacturing panels under EMS at the primary
 * tier instead of duplicating them as secondary.
 */

import {
  UNIVERSAL_PANELS,
  DOMAIN_PANELS,
  MODIFIER_PANELS,
  NEW_AGE_PANELS,
  BUSINESS_TYPES,
} from "./businessTypeConfig";

const DEFAULT_FLAGS = {
  imports: false,
  exports: false,
  has_physical_retail: false,
  b2b: false,
  b2c: false,
};

// ---------------------------------------------------------------------------
// Panel → real-value derivation — SHARED across every consumer of these
// panel definitions (Business Panels page + Benchmarking's domain-adaptive
// KPI strip). Moved here (Feb 2026, Benchmarking Phase 1) from BusinessPanels.jsx
// so both pages read the exact same "is this panel live?" / "what's the real
// value?" logic instead of two independently-drifting copies — one company,
// one truth, even for the small subset of panels that already have a real
// computation wired.
// ---------------------------------------------------------------------------

/**
 * Maps a panel.source string ("tally:debtors", "params:capacity", etc.) to
 * the dotted semantic field on the dashboard-data payload, so any consumer
 * can show an honest "Connect X" / dash state when the source isn't wired
 * yet. Panels without an explicit mapping fall through to a generic
 * "awaiting source" state — never a fabricated number.
 */
export const PANEL_SOURCE_TO_SEMANTIC = {
  // Tally-driven
  "tally:financial":           "raw.revenue_mtd",
  "tally:debtors":             "debtors.debtors_raw",
  "tally:creditors":           "raw.total_creditors",
  "tally:stock":               "inventory.items_raw",
  "tally:gst":                 "gst.gstin",
  "tally:sales+stock":         "inventory.items_raw",
  "tally:stock+sales":         "inventory.items_raw",
  "tally:stock+params":        "inventory.items_raw",
  "tally:job_orders":          "job_costing.jobs",
  "tally:fx":                  null,
  "tally:fx+creditors":        null,
  "tally:fx+debtors":          null,
  "tally:financial+params":    "raw.revenue_mtd",
  // CRM-driven
  "crm:opportunities":         "zoho.crm.deal_total_amount",
  // Tally P&L payroll (awaiting source — P&L extraction is a separate open bug)
  "tally:pnl_payroll":         null,
  // Param-driven (filled via Setup Checklist → Company Parameters)
  "params:capacity":           "params.capacity_utilization_pct",
  "params:quality":            "params.scrap_rejection_pct",
  "params:delivery":           "params.on_time_delivery_pct",
  "params:marketing+ads":      "params.marketing_spend",
  "params:retention":          "params.ltv",
  "params:channels":           "params.channel_mix",
  "params:fulfillment":        "params.rto_rate_pct",
  "params:orders":             "params.avg_order_value",
  "params:payouts":            "params.marketplace_payout_days",
  "params:bom":                "params.bom_cost_volatility_pct",
  "params:supply_chain":       "params.component_lead_time_days",
  "params:product":            "params.npi_cycle_days",
  "params:timesheets":         "params.billable_utilization_pct",
  "params:project_costing":    "params.project_margin_pct",
  "params:billing":            "params.realization_rate_pct",
  "params:store_grid":         "params.sales_per_sqft",
  "params:footfall":           "params.footfall_count",
  "params:pos":                "params.pos_payment_mix",
  "params:inventory":          "params.shrinkage_pct",
  "params:beds":                "params.bed_occupancy_pct",
  "params:department_revenue": "params.department_revenue",
  "params:tpa":                "params.tpa_claim_cycle_days",
  "params:payer":              "params.payer_mix",
  "params:doctor_payout":      "params.doctor_payout",
  "params:ipd":                "params.length_of_stay_days",
  "params:landed_cost":        "params.landed_cost_per_sku",
  "params:incentives":         "params.export_incentives_inr",
  // Ads
  "ads:meta+google":           "ads.roas",
  // Billing platform
  "billing:subscriptions":     "billing.mrr",
  // Derived from above
  "derived:cac+ltv":            "params.ltv",
  "derived:aov+cogs+ship":      "params.avg_order_value",
  "derived:scenario_engine":    null,
  // Services-domain derivations (already exposed by calc engine)
  "derived:tally:revenue+params:employee_count": "ceo.revenue_per_employee",
};

function fmtCroreLakh(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(1)}L`;
  if (Math.abs(v) >= 1000)        return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

/** Returns the semantic dashboard-data field path for a panel, or null. */
export function semanticFieldForPanel(panel) {
  return PANEL_SOURCE_TO_SEMANTIC[panel.source] || null;
}

/**
 * Returns a pretty-printed real value for a Wire-Now panel, or null if the
 * data isn't available / not yet computed anywhere. Pure read — no
 * fabrication. Only a handful of panel ids have a real computation today;
 * every other panel intentionally returns null (caller shows an honest
 * dash / "awaiting source" state, never an invented number).
 */
export function derivePanelValue(panel, customerData) {
  if (!customerData) return null;
  switch (panel.id) {
    case "revenue_per_employee": {
      const v = customerData?.ceo?.revenue_per_employee;
      return v != null ? fmtCroreLakh(v) : null;
    }
    case "realization_rate": {
      const v = customerData?.efficiency?.collection_efficiency;
      return v != null ? `${Number(v).toFixed(1)}%` : null;
    }
    case "billing_collection_cycle": {
      const v = customerData?.efficiency?.dso_days;
      return v != null ? `${Math.round(Number(v))} days` : null;
    }
    case "engagement_pipeline": {
      const crm = customerData?.zoho?.crm || {};
      const total = crm.deal_total_amount;
      const count = crm.deal_count;
      if (!count) return null;
      return `${count} deal${count === 1 ? "" : "s"} · ${fmtCroreLakh(total) || "₹—"}`;
    }
    default: return null;
  }
}

/** Normalise a possibly-undefined profile into the canonical shape. */
export function normaliseBusinessProfile(profile) {
  return {
    primary_business_model: profile?.primary_business_model || null,
    additional_models:      Array.isArray(profile?.additional_models) ? profile.additional_models.filter(Boolean) : [],
    modifier_flags:         { ...DEFAULT_FLAGS, ...(profile?.modifier_flags || {}) },
  };
}

/** Returns the list of types that a primary type "inherits" (e.g. EMS → manufacturing). */
function inheritedTypesFor(typeId) {
  if (!typeId || !BUSINESS_TYPES[typeId]) return [];
  return BUSINESS_TYPES[typeId].inherits || [];
}

/** Append a panel with a given tier — respecting the de-dup set. */
function addPanel(out, seen, panel, tier, extra = {}) {
  if (!panel || !panel.id || seen.has(panel.id)) return;
  seen.add(panel.id);
  out.push({ ...panel, tier, ...extra });
}

/** Evaluate a new-age panel's gating rule against the resolved profile. */
function newAgeGateMatches(gating, profile) {
  if (!gating) return true;
  if (gating.always) return true;
  if (Array.isArray(gating.anyFlag)) {
    return gating.anyFlag.some((f) => !!profile.modifier_flags[f]);
  }
  if (gating.anyTypeOrFlag) {
    const { types = [], flags = [] } = gating.anyTypeOrFlag;
    const primary = profile.primary_business_model;
    const all = new Set([primary, ...(profile.additional_models || [])].filter(Boolean));
    if (types.some((t) => all.has(t))) return true;
    if (flags.some((f) => !!profile.modifier_flags[f])) return true;
    return false;
  }
  return true;
}

/**
 * Main entry point.
 *
 * @returns {{panels: Array, tiers: {core, primary, secondary, modifier, strategic}, profile}}
 */
export function resolveBusinessPanels(rawProfile) {
  const profile = normaliseBusinessProfile(rawProfile);
  const seen = new Set();
  const out  = [];

  // 1. Universal core — always first.
  for (const p of UNIVERSAL_PANELS) {
    addPanel(out, seen, p, "core");
  }

  // 2. Primary business type + its inherits — primary tier.
  //    Stub types contribute NO domain panels (universal-core + modifiers
  //    + strategic only). Their inherits chain is also skipped because
  //    the populated tag on the primary type drives the gate.
  const primary = profile.primary_business_model;
  if (primary && DOMAIN_PANELS[primary]?.populated) {
    // Inherits first, so the inheritor's own panels visually sit beneath
    // the inherited ones (e.g. EMS shows manufacturing panels then EMS-
    // specific ones).
    for (const inherited of inheritedTypesFor(primary)) {
      if (!DOMAIN_PANELS[inherited]?.populated) continue;
      for (const p of (DOMAIN_PANELS[inherited]?.panels || [])) {
        addPanel(out, seen, p, "primary", { originType: inherited, originPrimaryType: primary, inherited: true });
      }
    }
    for (const p of (DOMAIN_PANELS[primary]?.panels || [])) {
      addPanel(out, seen, p, "primary", { originType: primary, originPrimaryType: primary });
    }
  }

  // 3. Additional (secondary) types — secondary tier, collapsible groups.
  //    Same populated-gate as primary so stubs in additional_models[] don't
  //    pollute the resolved list.
  for (const sec of profile.additional_models) {
    if (sec === primary) continue;
    if (!DOMAIN_PANELS[sec]?.populated) continue;
    for (const inherited of inheritedTypesFor(sec)) {
      if (!DOMAIN_PANELS[inherited]?.populated) continue;
      for (const p of (DOMAIN_PANELS[inherited]?.panels || [])) {
        addPanel(out, seen, p, "secondary", { originType: inherited, originSecondaryType: sec, inherited: true });
      }
    }
    for (const p of (DOMAIN_PANELS[sec]?.panels || [])) {
      addPanel(out, seen, p, "secondary", { originType: sec, originSecondaryType: sec });
    }
  }

  // 4. Modifier-gated panels — modifier tier (type-agnostic).
  for (const [flag, panels] of Object.entries(MODIFIER_PANELS)) {
    if (!profile.modifier_flags[flag]) continue;
    for (const p of panels) {
      addPanel(out, seen, p, "modifier", { originFlag: flag });
    }
  }

  // 5. New-age strategic layer — gated by rules.
  for (const p of NEW_AGE_PANELS) {
    if (!newAgeGateMatches(p.gating, profile)) continue;
    addPanel(out, seen, p, "strategic");
  }

  // Group counts for the UI badge ("4 modules unlocked for D2C + Imports").
  const tiers = { core: 0, primary: 0, secondary: 0, modifier: 0, strategic: 0 };
  for (const r of out) tiers[r.tier] += 1;

  return { panels: out, tiers, profile };
}

/**
 * Convenience: list the populated types selected on this profile.
 * Used by the page header to summarise the active configuration.
 */
export function activePopulatedTypes(profile) {
  const np = normaliseBusinessProfile(profile);
  const result = [];
  if (np.primary_business_model && BUSINESS_TYPES[np.primary_business_model]?.populated) {
    result.push({ id: np.primary_business_model, tier: "primary" });
  }
  for (const s of np.additional_models) {
    if (s !== np.primary_business_model && BUSINESS_TYPES[s]?.populated) {
      result.push({ id: s, tier: "secondary" });
    }
  }
  return result;
}

/**
 * Convenience: list the stub types selected on this profile (the ones that
 * render universal-core + "Module pack coming soon" banner).
 */
export function activeStubTypes(profile) {
  const np = normaliseBusinessProfile(profile);
  const result = [];
  if (np.primary_business_model && !BUSINESS_TYPES[np.primary_business_model]?.populated) {
    result.push({ id: np.primary_business_model, tier: "primary" });
  }
  for (const s of np.additional_models) {
    if (s !== np.primary_business_model && !BUSINESS_TYPES[s]?.populated) {
      result.push({ id: s, tier: "secondary" });
    }
  }
  return result;
}
